import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router';
import { waLink } from '@tiffinify/core';
import type { NeedsYouDto } from '@tiffinify/contracts';
import { TiffinMini, PageLoader, Badge, BtnPrimary, BtnSecondary, Card, CardTitle, Chip, Chips, Empty, Icon, IconBtn, Mono, RingAvatar, Section, Skel, TaskTitle, TopBar, LoadError } from '../components/ui';
import { endLabel, formatInr, mealsLabel } from '../lib/format';
import { WhatsAppText } from '../lib/whatsapp';
import { useBrief, useMessage, useToday } from '../lib/queries';

const TYPES = [
  { id: 'renewal', label: 'Renewal' },
  { id: 'payment', label: 'Payment due' },
  { id: 'status', label: 'Status' },
] as const;
type Type = (typeof TYPES)[number]['id'];
const typeFrom = (v: string | null): Type => TYPES.find((t) => t.id === v)?.id ?? 'renewal';
const typeFor = (n: Pick<NeedsYouDto, 'reasons'>): Type => (n.reasons.includes('ending') ? 'renewal' : 'payment');

/** One step of "Remind all": which person of how many, and the way to the next. */
interface Run {
  type: Type;
  step: number;
  total: number;
  /** First name of the next person; none on the last step. */
  nextName?: string;
  onNext: (sent: boolean) => void;
}

/** Keyed by plan: another customer's screen never inherits this one's edited text. */
export function Remind() {
  const { subId = '' } = useParams();
  return <RemindFor key={subId} subId={subId} />;
}

/**
 * "Remind all": everyone on Today's Needs you, one after another. The list is the one
 * on screen when the run starts, so it doesn't reshuffle as payments come in.
 */
export function RemindAll() {
  const today = useToday();
  const [list, setList] = useState<NeedsYouDto[] | null>(null);
  const [step, setStep] = useState(0);
  const [sent, setSent] = useState(0);

  useEffect(() => { if (!list && today.data) setList(today.data.needsYou); }, [list, today.data]);

  const head = (
    <>
      <TopBar to="/" label="Back to Today" />
      <TaskTitle title="Send reminders" sub="WhatsApp opens with each message ready" />
    </>
  );
  if (!list && today.isError) return <main className="main task">{head}<LoadError what="today’s list" onRetry={() => void today.refetch()} /></main>;
  if (!list) return <main className="main task">{head}<PageLoader /></main>;

  if (step >= list.length) {
    return (
      <main className="main task">
        <TopBar to="/" label="Back to Today" />
        <TaskTitle title={list.length ? 'Reminders done' : 'No one to remind'} />
        <Card>
          <Empty title={list.length ? `Sent ${sent} of ${list.length}` : 'All clear'}>
            {list.length ? (sent < list.length ? `${list.length - sent} skipped. They stay on Today until their plan or payment changes.` : 'Everyone on Needs you has their message.') : 'No plans ending soon and nothing to collect.'}
          </Empty>
        </Card>
        <div className="cta-end"><BtnPrimary label="Back to Today" icon="check" to="/" /></div>
      </main>
    );
  }

  const n = list[step]!;
  const next = list[step + 1];
  return (
    <RemindFor
      key={n.subscriptionId}
      subId={n.subscriptionId}
      run={{
        type: typeFor(n),
        step: step + 1,
        total: list.length,
        nextName: next?.name.split(' ')[0],
        onNext: (didSend) => { if (didSend) setSent((c) => c + 1); setStep((i) => i + 1); },
      }}
    />
  );
}

function RemindFor({ subId, run }: { subId: string; run?: Run }) {
  const [params, setParams] = useSearchParams();
  const nav = useNavigate();
  const [runType, setRunType] = useState<Type>(run?.type ?? 'renewal');
  const type = run ? runType : typeFrom(params.get('type'));
  const pickType = (t: Type) => (run ? setRunType(t) : setParams({ type: t }, { replace: true }));
  // In a run, opening WhatsApp turns the main button into "Next".
  const [opened, setOpened] = useState(false);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<string | null>(null);

  const briefQ = useBrief(subId);
  const brief = briefQ.data;
  const msg = useMessage(subId, type);
  const today = useToday().data;

  // A new message type (picked here, or by going back) starts from its generated text again.
  useEffect(() => { setDraft(null); setEditing(false); }, [type]);

  const text = draft ?? msg.data?.text ?? '';
  const phone = msg.data?.phone || brief?.phone || '';
  const link = phone && text ? waLink(phone, text) : '';
  // The previous type's text, kept on screen while the new one is written: shown, but not sendable.
  const stale = msg.isPlaceholderData;
  const writing = msg.isPending || stale;
  const others = (today?.needsYou ?? []).filter((n) => n.subscriptionId !== subId).slice(0, 3);

  const head = (
    <>
      <TopBar to="/" label="Back to Today" />
      {run
        ? <TaskTitle title="Send reminders" sub={`${run.step} of ${run.total} · WhatsApp opens with the message ready`} />
        : <TaskTitle title="Send reminder" sub="WhatsApp opens with the message ready" />}
    </>
  );
  if (briefQ.isError && !brief) return <main className="main task">{head}<LoadError what="this customer" onRetry={() => void briefQ.refetch()} /></main>;
  if (!brief) return <main className="main task">{head}<PageLoader /></main>;

  const ending = brief.state === 'active' && brief.daysToEnd >= 0 && brief.daysToEnd <= 3;
  return (
    <main className="main task">
      {head}

      <Card pad="sm">
        <div className="row remind-who">
          <RingAvatar name={brief.name} frac={Math.max(0, Math.min(1, brief.daysToEnd / 7))} color={ending ? 'var(--chilli)' : 'var(--ink)'} />
          <Link to={`/s/${brief.subscriberId}`} className="grow remind-who-name">
            <b>{brief.name}</b>
            <span className="sub">{[brief.area, mealsLabel(brief.slot)].filter(Boolean).join(' · ')}</span>
          </Link>
          {ending ? <Badge kind="end">{endLabel(brief.daysToEnd, brief.endDate)}</Badge> : brief.due > 0 ? <Badge kind="due">{formatInr(brief.due)} due</Badge> : null}
        </div>
      </Card>

      <Chips label="Message type">
        {TYPES.map((t) => <Chip key={t.id} on={type === t.id} onClick={() => pickType(t.id)}>{t.label}</Chip>)}
      </Chips>

      <Card pad>
        <CardTitle
          title="Message"
          right={
            <span className="row">
              {writing && <span className="busy-mark" role="status" aria-label="Writing the message"><TiffinMini /></span>}
              <button type="button" className="text-link edit-toggle" disabled={writing || !text} onClick={() => { setDraft(text); setEditing((e) => !e); }}>
                <Icon name="edit" size={16} sw={2.2} />{editing ? 'Done' : 'Edit'}
              </button>
            </span>
          }
        />
        {msg.isPending ? <div className="bubble" aria-hidden="true"><Skel w={240} /><Skel w={200} /><Skel w={260} /><Skel w={120} /></div>
          : editing ? <textarea className="plain" aria-label="Message text" value={text} onChange={(e) => setDraft(e.target.value)} rows={8} />
          : <div className={`bubble${stale ? ' stale' : ''}`}><WhatsAppText text={text} /></div>}
      </Card>

      {run && opened ? (
        <>
          <BtnPrimary label={run.nextName ? `Next: ${run.nextName}` : 'Finish'} icon={run.nextName ? 'arrow' : 'check'} onClick={() => run.onNext(true)} />
          <BtnSecondary label="Open WhatsApp again" icon="chat" href={link} />
        </>
      ) : (
        <BtnPrimary label="Open in WhatsApp" icon="chat" href={link} disabled={!link || writing} onClick={run ? () => setOpened(true) : undefined} />
      )}
      <p className="sub center">You tap Send in WhatsApp. Nothing goes out by itself.</p>
      {run && !opened && (
        <p className="center"><button type="button" className="link" onClick={() => run.onNext(false)}>Skip {brief.name.split(' ')[0]}</button></p>
      )}

      {!run && others.length > 0 && (
        <Section title="Next to remind" meta={<Mono>{others.length} more</Mono>}>
          <Card>
            {others.map((n) => (
              <div className="person" key={n.subscriptionId}>
                <Link to={`/s/${n.subscriberId}`}>
                  <RingAvatar name={n.name} frac={Math.max(0, Math.min(1, n.daysToEnd / 7))} color="var(--chilli)" />
                  <span className="who"><b>{n.name}</b><small>{n.reasons.includes('ending') ? endLabel(n.daysToEnd, n.endDate) : `${formatInr(n.due)} due`}</small></span>
                </Link>
                <IconBtn icon="chat" label={`Remind ${n.name.split(' ')[0]} on WhatsApp`} onClick={() => nav(`/remind/${n.subscriptionId}?type=${n.reasons.includes('ending') ? 'renewal' : 'payment'}`)} />
              </div>
            ))}
          </Card>
        </Section>
      )}
    </main>
  );
}
